import { z } from 'zod';
/**
 * Domain vocabulary shared by the API (validation, Drizzle enums) and the web
 * client (labels, tags). Values are English identifiers; French labels live in
 * the web app.
 */
/** Portal roles. Derived from the Entra `roles` claim at login, never stored as truth. */
export const PORTAL_ROLES = ['requester', 'referent', 'po'];
export const portalRoleSchema = z.enum(PORTAL_ROLES);
export const REQUEST_TYPES = ['bug', 'evolution', 'help'];
export const requestTypeSchema = z.enum(REQUEST_TYPES);
/**
 * Request statuses. The six displayed statuses of the lifecycle, plus the two
 * exits of qualification (refuse, mark as duplicate) shown in the state diagram
 * of « Cycle de vie d'une demande ».
 * « En attente de réponse » is NOT a status: it is derived from messages.
 */
export const REQUEST_STATUSES = [
    'submitted',
    'qualifying',
    'qualified',
    'planned',
    'in_progress',
    'delivered',
    'refused',
    'duplicate',
];
export const requestStatusSchema = z.enum(REQUEST_STATUSES);
/** The two exits of qualification (ADR-005: their own pill, a banner instead of the progress bar on 03). */
export const CLOSING_REQUEST_STATUSES = ['refused', 'duplicate'];
/** The six statuses of the progress bar, offered by the status filters and the « Statut » list of screen 08. */
export const DISPLAYED_REQUEST_STATUSES = [
    'submitted',
    'qualifying',
    'qualified',
    'planned',
    'in_progress',
    'delivered',
];
/**
 * A request is in the backlog of its product (screen 09) once qualified and
 * until delivered. Bugs and evolutions both enter the priority order (arbitrage
 * of 2026-10-19 on mock-up 09, which contradicts « Cycle de vie d'une demande »:
 * reported, not settled here); « Aide / accès » stays out of the backlog.
 */
export const BACKLOG_STATUSES = ['qualified', 'planned', 'in_progress'];
export const BACKLOG_TYPES = ['bug', 'evolution'];
/** A request without product cannot reach these statuses (ADR-003): no Jira, no backlog, no version without a product. */
export const STATUSES_REQUIRING_PRODUCT = ['qualified', 'planned', 'in_progress', 'delivered'];
/** Statuses a Jira status can be mapped to (screen 12), or null for « Ne pas suivre ». */
export const JIRA_MAPPABLE_STATUSES = ['planned', 'in_progress', 'delivered'];
export const IMPACTS = ['blocking', 'annoying', 'minor'];
export const impactSchema = z.enum(IMPACTS);
/** Set by the PO only. There is no « priorité souhaitée ». */
export const PRIORITIES = ['critical', 'high', 'normal', 'low'];
export const prioritySchema = z.enum(PRIORITIES);
export const ESTIMATES = ['XS', 'S', 'M', 'L', 'XL'];
export const estimateSchema = z.enum(ESTIMATES);
export const MESSAGE_VISIBILITIES = ['public', 'internal'];
export const messageVisibilitySchema = z.enum(MESSAGE_VISIBILITIES);
export const PRODUCT_TYPES = ['transverse', 'media_site', 'project'];
export const productTypeSchema = z.enum(PRODUCT_TYPES);
export const RELEASE_NOTE_STATES = ['draft', 'published'];
export const releaseNoteStateSchema = z.enum(RELEASE_NOTE_STATES);
/** State of a version, changed by hand by the PO (ADR-005). « Livrée » in the mock-ups is `in_production`. */
export const VERSION_STATES = ['to_plan', 'in_development', 'in_testing', 'in_production'];
export const versionStateSchema = z.enum(VERSION_STATES);
/**
 * Role of the author of a message, copied from the session when it is sent
 * (ADR-005). Used to READ the history (indicator, display), never to authorize.
 */
export const MESSAGE_AUTHOR_ROLES = PORTAL_ROLES;
/** How a request reached the portal (ADR-004). `portal`: the form of screen 02. */
export const INTAKE_CHANNELS = ['portal', 'phone', 'email', 'teams', 'meeting', 'other'];
export const intakeChannelSchema = z.enum(INTAKE_CHANNELS);
/** Item of a release note (ADR-005): a new feature or a fix. Counters are computed from them. */
export const RELEASE_NOTE_ITEM_TYPES = ['feature', 'fix'];
export const releaseNoteItemTypeSchema = z.enum(RELEASE_NOTE_ITEM_TYPES);
/** Origin of the last change on a Jira link, used to break the portal → Jira → webhook loop. */
export const JIRA_CHANGE_ORIGINS = ['portal', 'jira'];
/** Human-readable reference shown to business teams: PP-001, PP-042, PP-1042. */
export function formatRequestReference(referenceNumber) {
    return `PP-${String(referenceNumber).padStart(3, '0')}`;
}
/**
 * Outcome of a duplicate suggestion (ADR-009), journaled to tune the threshold.
 * - `subscribed`: « C'est le même problème » or « M'abonner et ajouter mon contexte »;
 * - `sent_anyway`: « C'est différent » on screen 16, the request was created;
 * - `ignored`: shown, not chosen (request sent from the form, « Revenir au formulaire », « Annuler »);
 * - `missed`: marked as duplicate by the PO without having been suggested.
 */
export const DUPLICATE_SUGGESTION_OUTCOMES = ['subscribed', 'sent_anyway', 'ignored', 'missed'];
/** Where a suggestion was shown: side panel of 02 / 15, screen 16, or never (a miss found on 08). */
export const DUPLICATE_SUGGESTION_PLACES = ['panel', 'prompt', 'qualification'];
//# sourceMappingURL=domain.js.map